import axios from 'axios';

/**
 * Chunked file uploader for the Storage / FileManager page.
 *
 * Splits each File into chunks and POSTs them to `/api/storage/upload`
 * (handled by `StorageController@upload`). The server accumulates chunks
 * in `chunks/{sessionId}/{fileId}/`, merges them once all chunks have
 * arrived, then writes a `ProjectFile` DB record — same disk location
 * and DB shape as the legacy `/api/uploadfiles` endpoint.
 *
 * Why chunked: each request stays well under nginx's `client_body_timeout`
 * even on slow connections, eliminating the `ERR_CONNECTION_RESET` and
 * `413` failures that happen when a single 300MB upload runs longer than
 * the timeout window.
 *
 * Performance characteristics (Tier 1):
 *   - Per-file chunks upload concurrently (default 3 in flight)
 *   - Multiple files upload concurrently (default 3 in flight)
 *   - Default chunk size 10MB — fewer requests = less HTTP overhead
 *   - Server merges chunks based on count-on-disk, not arrival order, so
 *     out-of-order arrival is safe
 *
 * Intentionally not built on `uploadManagerService.ts`, which is tightly
 * coupled to the Import Wizard's `ImportFile` / `ImportSession` data
 * model. Keep this one focused.
 */

export interface ChunkedUploadMetadata {
  /** Project / job to associate the file with. */
  job_id?: number | string;
  /**
   * Existing `ProjectFolder.id` (NOT folder_no) to store the file under.
   * If omitted, the file lands at the filemanager root.
   */
  targetFolder?: number | string | null;
  /** Optional override for the original filename. */
  originalName?: string;
}

export interface ChunkedUploadOptions {
  /** API base URL — typically `${import.meta.env.VITE_API_URL}/api`. */
  apiUrl: string;
  /** Bearer token for the user. */
  authToken: string;
  /**
   * Endpoint path appended to apiUrl. Defaults to `/storage/upload`
   * (StorageController). Pass a different path to target a different
   * controller — e.g., `/uploadfilesestimate` for estimate documents.
   */
  endpoint?: string;
  /**
   * Extra form fields appended to every chunk POST. Use this for
   * endpoint-specific top-level fields like `estimate_id` that aren't
   * part of the generic metadata blob.
   */
  extraFields?: Record<string, string | number | boolean>;
  /** Bytes per chunk. Defaults to 10MB. */
  chunkSize?: number;
  /**
   * Max chunks of the SAME file to upload in parallel.
   * Defaults to 3. Set to 1 for strictly sequential.
   */
  chunkConcurrency?: number;
  /**
   * Max files to upload in parallel (only used by uploadFilesChunked).
   * Defaults to 3. Set to 1 for strictly sequential.
   */
  fileConcurrency?: number;
  /** Server-side metadata sent with every chunk. */
  metadata?: ChunkedUploadMetadata;
  /** Called with 0..100 as bytes complete. */
  onProgress?: (percent: number) => void;
  /** Optional cancel signal — abort on the first failed chunk. */
  signal?: AbortSignal;
}

export interface ChunkedUploadResult {
  success: boolean;
  message?: string;
  error?: string;
}

const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB

// Tunable defaults. Read once at module load from Vite env so ops can
// adjust upload parallelism via .env without touching code or call sites:
//   VITE_UPLOAD_CHUNK_CONCURRENCY=6   # parallel chunks per file
//   VITE_UPLOAD_FILE_CONCURRENCY=3    # parallel files (uploadFilesChunked only)
// Per-call options.chunkConcurrency / fileConcurrency still override.
// Bumping these only helps when the uplink is NOT already saturated AND
// the server can keep up with parallel chunk merges.
function envInt(name: string, fallback: number): number {
  const raw = (import.meta as any)?.env?.[name];
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

const DEFAULT_CHUNK_CONCURRENCY = envInt('VITE_UPLOAD_CHUNK_CONCURRENCY', 3);
const DEFAULT_FILE_CONCURRENCY = envInt('VITE_UPLOAD_FILE_CONCURRENCY', 3);

function generateId(): string {
  // crypto.randomUUID is available in all modern browsers we target;
  // fallback keeps things working in older WebViews if any.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Upload a single File as a series of chunks. Resolves once all chunks
 * have been accepted by the server (and the server has merged + saved
 * the final file). Aborts on the first chunk that fails — any in-flight
 * chunks complete normally; remaining queued chunks are skipped.
 */
export async function uploadFileChunked(
  file: File,
  options: ChunkedUploadOptions
): Promise<ChunkedUploadResult> {
  const chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
  const concurrency = Math.max(
    1,
    Math.min(options.chunkConcurrency ?? DEFAULT_CHUNK_CONCURRENCY, totalChunks)
  );
  const sessionId = generateId();
  const fileId = generateId();
  const originalName = options.metadata?.originalName ?? file.name;

  const metadataPayload = {
    ...options.metadata,
    originalName,
    totalFileSize: file.size,
  };

  // Pending work — chunk indices to upload. Workers pull from the head.
  const queue: number[] = [];
  for (let i = 0; i < totalChunks; i++) queue.push(i);

  let aborted = false;
  let firstError: string | null = null;

  // Per-chunk byte counters. Progress is reported off the running total
  // of bytes uploaded so the bar moves DURING a chunk's upload, not just
  // when one finishes — important for large chunks on slow connections
  // where a single chunk can take 30+ seconds and the UI would otherwise
  // appear frozen at the previous percentage.
  const chunkBytesUploaded = new Array<number>(totalChunks).fill(0);
  const chunkSizes = new Array<number>(totalChunks);
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    chunkSizes[i] = Math.min(chunkSize, file.size - start);
  }

  const reportProgress = () => {
    if (!options.onProgress) return;
    let uploaded = 0;
    for (const b of chunkBytesUploaded) uploaded += b;
    const pct = file.size > 0 ? Math.min(100, Math.round((uploaded / file.size) * 100)) : 100;
    options.onProgress(pct);
  };

  const uploadChunk = async (chunkIndex: number): Promise<void> => {
    if (aborted) return;
    if (options.signal?.aborted) {
      aborted = true;
      if (!firstError) firstError = 'Upload cancelled';
      return;
    }

    const start = chunkIndex * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunkBlob = file.slice(start, end);

    const formData = new FormData();
    // Preserve the original filename on each chunk so MIME detection on
    // the server side has a hint (some chunks past the first don't carry
    // recognisable magic bytes for the original type).
    formData.append('file', chunkBlob, file.name);
    formData.append('sessionId', sessionId);
    formData.append('fileId', fileId);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('totalChunks', String(totalChunks));
    formData.append('metadata', JSON.stringify(metadataPayload));
    // Endpoint-specific top-level fields (e.g., `estimate_id`).
    if (options.extraFields) {
      for (const [key, value] of Object.entries(options.extraFields)) {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      }
    }

    const endpoint = options.endpoint ?? '/storage/upload';
    try {
      const response = await axios.post(`${options.apiUrl}${endpoint}`, formData, {
        // Don't set `Content-Type` here — axios auto-generates it from the
        // FormData with the correct `boundary=...` parameter. Setting it
        // manually to a bare `multipart/form-data` strips the boundary and
        // PHP's multipart parser can't split the body, leaving $_FILES
        // empty and the controller seeing "No file uploaded".
        headers: {
          Authorization: `Bearer ${options.authToken}`,
        },
        signal: options.signal,
        onUploadProgress: (e) => {
          // Cap at the chunk's nominal size to defend against axios
          // reporting loaded > total in some edge cases.
          const loaded = Math.min(e.loaded, chunkSizes[chunkIndex]);
          chunkBytesUploaded[chunkIndex] = loaded;
          reportProgress();
        },
      });

      if (response.data?.success === false) {
        if (!firstError) firstError = response.data.error || 'Upload failed';
        aborted = true;
        return;
      }

      // Lock in the full chunk size on success in case the byte-progress
      // callback under-counted at the tail.
      chunkBytesUploaded[chunkIndex] = chunkSizes[chunkIndex];
      reportProgress();
    } catch (err: any) {
      if (!firstError) {
        firstError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Upload failed';
      }
      aborted = true;
    }
  };

  // Worker pool: each worker loops, pulling the next chunk index off the
  // queue, until empty or aborted.
  const worker = async (): Promise<void> => {
    while (!aborted) {
      const chunkIndex = queue.shift();
      if (chunkIndex === undefined) return;
      await uploadChunk(chunkIndex);
    }
  };

  await Promise.all(Array.from({ length: concurrency }, () => worker()));

  if (aborted) {
    return { success: false, error: firstError || 'Upload failed' };
  }
  return { success: true, message: 'File uploaded successfully' };
}

/**
 * Upload multiple files concurrently. Aggregates progress across all
 * files (single smooth 0..100, not per-file resets) and aborts on the
 * first file that fails — any files already in flight finish, remaining
 * queued files are skipped.
 */
export async function uploadFilesChunked(
  files: File[],
  options: ChunkedUploadOptions
): Promise<{ success: boolean; uploaded: number; failed: File | null; error?: string }> {
  if (files.length === 0) {
    return { success: true, uploaded: 0, failed: null };
  }

  const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1;
  const fileConcurrency = Math.max(
    1,
    Math.min(options.fileConcurrency ?? DEFAULT_FILE_CONCURRENCY, files.length)
  );

  // Track per-file uploaded bytes so we can aggregate across in-flight
  // files into one smooth overall progress.
  const fileBytes = new Map<File, number>();
  for (const f of files) fileBytes.set(f, 0);

  const reportProgress = () => {
    if (!options.onProgress) return;
    let totalUploaded = 0;
    for (const b of fileBytes.values()) totalUploaded += b;
    options.onProgress(Math.min(100, Math.round((totalUploaded / totalBytes) * 100)));
  };

  const fileQueue: File[] = [...files];
  let aborted = false;
  let firstFailure: { file: File; error: string } | null = null;
  let uploaded = 0;

  const fileWorker = async (): Promise<void> => {
    while (!aborted) {
      const file = fileQueue.shift();
      if (!file) return;

      const result = await uploadFileChunked(file, {
        ...options,
        onProgress: (filePercent) => {
          fileBytes.set(file, (filePercent / 100) * file.size);
          reportProgress();
        },
      });

      if (!result.success) {
        if (!firstFailure) {
          firstFailure = { file, error: result.error || 'Upload failed' };
        }
        aborted = true;
        return;
      }

      // Lock in the full file size on success in case the per-chunk
      // progress callbacks under-counted by rounding.
      fileBytes.set(file, file.size);
      uploaded++;
      reportProgress();
    }
  };

  await Promise.all(Array.from({ length: fileConcurrency }, () => fileWorker()));

  if (firstFailure !== null) {
    const failure: { file: File; error: string } = firstFailure;
    return {
      success: false,
      uploaded,
      failed: failure.file,
      error: failure.error,
    };
  }

  if (options.onProgress) options.onProgress(100);
  return { success: true, uploaded, failed: null };
}

/**
 * Backward-compat alias. Old callers used the "Sequential" name when the
 * implementation was strictly sequential; behaviour is now concurrent by
 * default but the contract (and progress shape) is unchanged. Pass
 * `fileConcurrency: 1` if you genuinely need sequential file uploads.
 */
export const uploadFilesChunkedSequential = uploadFilesChunked;
